import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { alpha, styled } from "@mui/material/styles";

import "./ValueReset.css";
import { isAtParamDefault, TParamDefault } from "../../parametersProcessor/paramDefaults";

/**
 * Egy mező + a hozzá tartozó "visszaállítás alapértelmezettre" ikon-gomb.
 *
 * SZÁNDÉKOSAN BUTA: nem oldja fel az alapértéket, csak megkapja (`paramDefault`) — lásd
 * paramDefaults.ts. Így ugyanez a komponens szolgálja ki
 *   - az egész paraméter-kártyát (PropertyContainerCard, a típus szerinti feloldással), ÉS
 *   - egy composite kártya EGY-EGY al-mezőjét (BackPassing kötés / ráhagyás / mélység),
 * ahol az al-mező alapértékét a kártya a saját, MÁR MEGLÉVŐ descriptor-helpereivel adja meg.
 *
 * Ha nincs alapértelmezés (`paramDefault === null`), a gyerekét adja vissza VÁLTOZATLANUL — ilyenkor
 * a DOM és a layout is pontosan olyan, mint a gomb bevezetése előtt.
 *
 * Az ikon a MUI `SettingsBackupRestore` (óramutatóval ellentétes nyíl + skála): a MUI készletében ez
 * jelenti a "gyári/alapértelmezett állapot visszaállítását" — a `RestartAlt` inkább újraindítást, a
 * `Replay` ismétlést, az `Undo` pedig az utolsó lépés visszavonását sugallná.
 *
 * STÍLUS: az elrendezés a ValueReset.css-ben; a SZÍNEK viszont kizárólag a témából
 * (UCThemeProvider `palette.primary.main` / `.light`), ezért azok CSS-in-JS-ben vannak — egy statikus
 * stíluslap nem látja a témát, ott csak a hexek másolata állhatna.
 */

/** A tooltip fix része; a zárójeles rész az ADOTT mező alapértelmezett értéke. */
export const RESET_TOOLTIP_LABEL = "Visszaállítás alapértelmezettre";

/**
 * A globális MuiIconButton téma-override fehér ikont ad (sötét felületekre szabva), ami a világos
 * kártyákon láthatatlan lenne — ezért kell a saját színezés.
 *
 * A DUPLÁZOTT `&&` SZÁNDÉKOS az állapot-szabályoknál. A `styled(IconButton)` a wrapper stílusait a
 * bázis IconButton stílusai ELŐTT szúrja be (a szülő renderel előbb), és a MUI saját szabályai
 * ugyanolyan specifikusak (`.css-base.Mui-disabled`), mint az egyszeres `&`-tel írtak — vagyis a
 * döntetlent a MUI nyerné, és az `action.disabled` szürkéje jönne ki. A `&&` megkettőzi az osztályt
 * a szelektorban, így a szabály beszúrási sorrendtől függetlenül érvényesül.
 */
const ResetIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    transition: theme.transitions.create(["color", "background-color"], {
        duration: theme.transitions.duration.shortest,
    }),
    // Hoveren SZÁNDÉKOSAN nem vált színt, csak háttér-tintet kap: a két zöld itt ÁLLAPOTOT jelent
    // (lásd lentebb), egy hover-szín összekeverné a kettőt.
    "&&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    // A SZÍN AZ ÁLLAPOT: sötét zöld (primary.main) = van mit visszaállítani, a gomb aktív;
    // világos zöld (primary.light) = a mező már az alapértelmezetten áll, nincs teendő.
    "&&.Mui-disabled": {
        color: theme.palette.primary.light,
    },
}));

interface IValueResetProps {
    /** A mező alapértelmezett értéke + emberi címkéje; `null` esetén nincs gomb. */
    paramDefault: TParamDefault | null;
    /** A mező MOSTANI értéke — ebből látszik, hogy már az alapértelmezetten áll-e. */
    currentValue: any;
    /** A visszaállítás: ugyanaz az útvonal, amit a mező kézi szerkesztése is használ. */
    onReset: (value: any) => void;
    children: React.ReactNode;
}

const ValueReset: React.FC<IValueResetProps> = ({ paramDefault, currentValue, onReset, children }) => {
    if (!paramDefault) return <>{children}</>;

    const title = `${RESET_TOOLTIP_LABEL} (${paramDefault.label})`;
    const atDefault = isAtParamDefault(currentValue, paramDefault.value);

    return (
        <div className="uc-value-reset">
            <div className="uc-value-reset__field">{children}</div>
            {/* A LETILTOTT gomb nem küld egér-eseményt, ezért a Tooltip a körülötte lévő elemen ül —
                különben épp az alapértelmezetten álló mezőnél tűnne el az az információ, amiért a
                gomb ki van rakva (mi is az alapérték). */}
            <Tooltip title={title} arrow>
                <div className="uc-value-reset__button">
                    <ResetIconButton
                        // A mezők közti Tab-bal járásból kimarad (a NumericInput léptető gombjaihoz
                        // hasonlóan): kiegészítő művelet, nem a beviteli útvonal része.
                        tabIndex={-1}
                        size="small"
                        aria-label={title}
                        disabled={atDefault}
                        onClick={() => onReset(paramDefault.value)}
                    >
                        <SettingsBackupRestoreIcon fontSize="small" />
                    </ResetIconButton>
                </div>
            </Tooltip>
        </div>
    );
};

export default ValueReset;
